import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  styled,
} from '@mui/material'
import {
  IconBrandMeta,
  IconBrandTiktok,
  IconBrandSpotify,
  IconBrandGoogle,
} from '@tabler/icons-react'
import { Platform, CampaignSetupFormValues, AdAccountOption, SocialPageOption } from '../types'
import StepNavigation from './StepNavigation'
import axiosInstance from '../../../../../utils/axiosInstance'

const PlatformCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => ({
  border: `2px solid ${selected ? theme.palette.secondary.main : theme.palette.divider}`,
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'border-color 0.2s',
  boxShadow: 'none',
  '&:hover': {
    borderColor: theme.palette.secondary.main,
  },
}))

const SectionCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  boxShadow: 'none',
  marginBottom: theme.spacing(3),
}))

interface PlatformConfig {
  id: Platform
  label: string
  subtitle: string
  icon: React.ReactNode
  color: string
}

const PLATFORMS: PlatformConfig[] = [
  {
    id: 'meta',
    label: 'Meta',
    subtitle: 'Facebook, Instagram, Threads',
    icon: <IconBrandMeta size={32} />,
    color: '#0866FF',
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    subtitle: 'TikTok Ads Manager',
    icon: <IconBrandTiktok size={32} />,
    color: '#000000',
  },
  {
    id: 'spotify',
    label: 'Spotify',
    subtitle: 'Spotify Ad Studio',
    icon: <IconBrandSpotify size={32} />,
    color: '#1DB954',
  },
  {
    id: 'google',
    label: 'Google',
    subtitle: 'Google Ads & YouTube',
    icon: <IconBrandGoogle size={32} />,
    color: '#4285F4',
  },
]

interface Props {
  platforms: Platform[]
  adAccountId: string
  socialPageId: string
  customerId?: string
  onChange: (field: keyof CampaignSetupFormValues, value: unknown) => void
  onNext: () => void
  onBack: () => void
  isFirstStep: boolean
  readOnly?: boolean
}

const PlatformStep: React.FC<Props> = ({
  platforms,
  adAccountId,
  socialPageId,
  customerId,
  onChange,
  onNext,
  onBack,
  isFirstStep,
  readOnly,
}) => {
  const [adAccounts, setAdAccounts] = useState<AdAccountOption[]>([])
  const [pages, setPages] = useState<SocialPageOption[]>([])

  useEffect(() => {
    if (!customerId) return
    axiosInstance
      .get(`/customer/${customerId}/ad-accounts`)
      .then((res) => {
        const data: (AdAccountOption & { isConnected?: boolean })[] = res.data || []
        setAdAccounts(data)
        // Pre-select the first connected account if nothing is selected yet
        if (!adAccountId) {
          const connected = data.find((a) => a.isConnected)
          if (connected) onChange('adAccountId', connected.id)
        }
      })
      .catch(() => {})

    axiosInstance
      .get(`/customer/${customerId}/pages`)
      .then((res) => {
        const data: (SocialPageOption & { isConnected?: boolean })[] = res.data || []
        setPages(data)
        // Pre-select the first connected page if nothing is selected yet
        if (!socialPageId) {
          const connected = data.find((p) => p.isConnected)
          if (connected) onChange('socialPageId', connected.id)
        }
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId])

  const togglePlatform = (id: Platform) => {
    if (platforms.includes(id)) {
      onChange(
        'platforms',
        platforms.filter((p) => p !== id)
      )
    } else {
      onChange('platforms', [...platforms, id])
    }
  }

  return (
    <Box>
      <SectionCard>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
            Select Platform
          </Typography>
          <Grid container spacing={2}>
            {PLATFORMS.map((platform) => {
              const isSelected = platforms.includes(platform.id)
              return (
                <Grid item xs={12} sm={6} key={platform.id}>
                  <PlatformCard
                    selected={isSelected}
                    onClick={() => !readOnly && togglePlatform(platform.id)}
                    sx={readOnly ? { cursor: 'default', opacity: isSelected ? 1 : 0.6 } : undefined}
                  >
                    <CardContent
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        '&:last-child': { pb: 2 },
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        color="secondary"
                        sx={{ p: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => togglePlatform(platform.id)}
                        disabled={readOnly}
                      />
                      <Box sx={{ color: platform.color, display: 'flex', alignItems: 'center' }}>
                        {platform.icon}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {platform.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {platform.subtitle}
                        </Typography>
                      </Box>
                    </CardContent>
                  </PlatformCard>
                </Grid>
              )
            })}
          </Grid>
        </CardContent>
      </SectionCard>

      <SectionCard>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
            Ad Account &amp; Page
          </Typography>
          {!customerId && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 2 }}>
              No customer linked to this order. Ad accounts and pages cannot be loaded.
            </Typography>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Ad Account *</InputLabel>
                <Select
                  value={adAccountId}
                  label="Ad Account *"
                  onChange={(e) => onChange('adAccountId', e.target.value)}
                  disabled={!customerId || readOnly}
                >
                  {adAccounts.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name} ({a.externalId})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Social Page *</InputLabel>
                <Select
                  value={socialPageId}
                  label="Social Page *"
                  onChange={(e) => onChange('socialPageId', e.target.value)}
                  disabled={!customerId || readOnly}
                >
                  {pages.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name} ({p.externalId})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {(() => {
                const selectedPage = pages.find((p) => p.id === socialPageId)
                if (!selectedPage?.instagramActorId) return null
                return (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      size="small"
                      label={`Instagram: ${selectedPage.instagramActorId}`}
                      color="secondary"
                      variant="outlined"
                    />
                  </Box>
                )
              })()}
            </Grid>
          </Grid>
        </CardContent>
      </SectionCard>

      <StepNavigation onBack={onBack} onNext={onNext} isFirstStep={isFirstStep} />
    </Box>
  )
}

export default PlatformStep
